"use client";

import {
  TEXT_DIM,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";
import { Breadcrumb } from "@/components/ui/molecules/breadcrumb";
import { useNoteLabel } from "@/hooks/use-notation";
import type { HarmonyTonality } from "./types";

interface HarmonyBreadcrumbProps {
  tonality: HarmonyTonality;
  leafLabel: string;
  /** Optional pill displayed after the leaf (e.g. functional role). */
  leafBadge?: string;
  onBackToOverview: () => void;
}

/**
 * Sticky breadcrumb shown above scale / chord / progression detail views.
 * Mirrors the mockup's `Harmonie › Do majeur › <leaf>` strip and exposes
 * an explicit «&nbsp;Vue d&apos;ensemble&nbsp;» back button, the atlas
 * (left rail) reflects the current selection but does not surface a
 * direct way back to the overview, so this navigation lives here.
 *
 * Ne porte plus que ce qui est propre à l'harmonie — le libellé de tonalité et
 * le retour à la vue d'ensemble. Le fil lui-même vit dans
 * `components/ui/molecules/breadcrumb.tsx` depuis que la bibliothèque en a
 * besoin aussi ; les styles y ont été repris à l'identique.
 */
export function HarmonyBreadcrumb({
  tonality,
  leafLabel,
  leafBadge,
  onBackToOverview,
}: HarmonyBreadcrumbProps) {
  const toNoteLabel = useNoteLabel();
  const tonalityLabel = `${toNoteLabel(tonality.tonic)} ${
    tonality.mode === "major" ? "majeur" : "mineur"
  }`;

  return (
    <Breadcrumb
      leading={
        <button
          type="button"
          onClick={onBackToOverview}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px 5px 8px",
            marginRight: 8,
            borderRadius: 6,
            border: `1px solid ${VIOLET_GLOW}`,
            background: "rgba(139,92,246,0.12)",
            color: VIOLET_BRIGHT,
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0",
            cursor: "pointer",
            transition: "background 120ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(139,92,246,0.28)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(139,92,246,0.12)";
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
            ←
          </span>
          Vue d&apos;ensemble
        </button>
      }
      items={[
        {
          label: "Harmonie",
          onClick: onBackToOverview,
          style: {
            color: VIOLET_BRIGHT,
            opacity: 0.75,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            fontWeight: 600,
          },
        },
        {
          label: tonalityLabel,
          onClick: onBackToOverview,
          style: { color: TEXT_DIM },
        },
      ]}
      current={leafLabel}
      badge={leafBadge}
    />
  );
}
